'use client';

/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/react';
import Button from '@egalite/ui/Buttons';
import ErrorList from '@egalite/ui/ErrorList';
import { InputField } from '@egalite/ui/Form';
import CheckboxGroup from '@egalite/ui/Form/CheckboxGroup';
import SelectField from '@egalite/ui/Form/SelectField';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Loading from '@egalite/ui/Loading';
import Typography from '@egalite/ui/Typography';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { FC, useEffect, useState } from 'react';
import authFetch from '../../lib/auth/authFetch';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/professionalData4';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import FormPage from './legacy_blocks/CandidateFormPage';
import save from './save';
import useSomeChangedEffect from './useSomeChangedEffect';

const ProfessionalData4Form: FC<{ edit?: boolean; onSuccess?: () => void }> = ({ edit = false, onSuccess = null }) => {
	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState(initialValues);

	const [logicProgrammingChecked, setLogicProgrammingChecked] = useState(false);
	const router = useRouter();

	const [formController, getFieldProps, { loading, submitting, success, errors, data }] = useForm({
		initialValues: candidateData,
		validationSchema,
		mutation: data => save('/api/v1/candidate/professionalData4', data),
		onSuccess:
			onSuccess != null
				? () => onSuccess()
				: () => {
						router.push(`/candidato/form/professionalDataExperienceIndex`, { scroll: true });
				  },
	});
	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					if (data.success === true) {
						const newValues = Object.keys(initialValues).reduce((acc, key) => {
							acc[key] = data.data[key] ?? initialValues[key];
							return acc;
						}, {});
						setCandidateData(newValues as typeof initialValues);
						setLoadingData(false);
					} else {
						notification.error('Erro ao carregar dados do candidato');
					}
				});
		}
	}, [formController, router, loadingData, notification]);

	useSomeChangedEffect(
		() => {
			if (logicProgrammingChecked === false) {
				formController.setFieldValue('logicProgrammingDescription', []);
			}
		},
		[formController, logicProgrammingChecked],
		[logicProgrammingChecked],
	);

	useEffect(() => {
		if (['Básico', 'Médio', 'Avançado'].includes(formController?.values.logicProgramming)) {
			setLogicProgrammingChecked(true);
		} else {
			setLogicProgrammingChecked(false);
		}
	}, [formController?.values]);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={3}
			formActualStep={4}
			formTotalSteps={5}
			progress={80}
			formTitle={'Conhecimentos'}
			// previousForm="/"
			showStepsMenu={false}
			// showBackButton={false}
			modal={edit}
		>
			<form onSubmit={formController.handleSubmit}>
				<div className="mb-16 md:mb-24">
					<Typography variant="headline-5" className="text-brand-main mb-16">
						Conhecimentos
					</Typography>

					<SelectField
						id="excel"
						pladeholder={'selecione...'}
						required={false}
						label={'Excel'}
						className='my-16'
						options={[
							{ value: 'Não', label: 'Não' },
							{ value: 'Básico', label: 'Básico' },
							{ value: 'Médio', label: 'Intermediário' },
							{ value: 'Avançado', label: 'Avançado' },
						]}
						{...getFieldProps('excel')}
					/>
					<SelectField
						id="word"
						pladeholder={'selecione...'}
						label={'Word'}
						className='my-16'
						required={false}
						options={[
							{ value: 'Não', label: 'Não' },
							{ value: 'Básico', label: 'Básico' },
							{ value: 'Médio', label: 'Intermediário' },
							{ value: 'Avançado', label: 'Avançado' },
						]}
						{...getFieldProps('word')}
					/>
					<SelectField
						id="powerpoint"
						pladeholder={'selecione...'}
						required={false}
						label={'Powerpoint'}
						className='my-16'
						options={[
							{ value: 'Não', label: 'Não' },
							{ value: 'Básico', label: 'Básico' },
							{ value: 'Médio', label: 'Intermediário' },
							{ value: 'Avançado', label: 'Avançado' },
						]}
						{...getFieldProps('powerpoint')}
					/>
					<SelectField
						id="logicProgramming"
						pladeholder={'selecione...'}
						required={false}
						label={'Lógica de Programação'}
						className='my-16'
						options={[
							{ value: 'Não', label: 'Não' },
							{ value: 'Básico', label: 'Básico' },
							{ value: 'Médio', label: 'Intermediário' },
							{ value: 'Avançado', label: 'Avançado' },
						]}
						{...getFieldProps('logicProgramming')}
					/>
					{logicProgrammingChecked && (
						<CheckboxGroup
							options={[
								'Administração de dados',
								'Back-end',
								'Data science',
								'Front-end',
								'Full-stack',
								'Mobile',
								'Segurança da informação',
								'UX/UI',
							]}
							{...getFieldProps({ name: 'logicProgrammingDescription', type: 'checkbox' })}
							descriptionFieldProps={{
								option: 'Outro',
								...getFieldProps('logicProgrammingDescriptionOther'),
							}}
						/>
					)}
					<SelectField
						id="english"
						name="english"
						pladeholder={'selecione...'}
						required={false}
						label={'Inglês'}
						className='my-16'
						options={[
							{ value: 'Não', label: 'Não' },
							{ value: 'Básico', label: 'Básico' },
							{ value: 'Médio', label: 'Intermediário' },
							{ value: 'Avançado', label: 'Avançado' },
						]}
						{...getFieldProps('english')}
					/>
					<SelectField
						id="spanish"
						name="spanish"
						pladeholder={'selecione...'}
						required={false}
						label={'Espanhol'}
						className='my-16'
						options={[
							{ value: 'Não', label: 'Não' },
							{ value: 'Básico', label: 'Básico' },
							{ value: 'Médio', label: 'Intermediário' },
							{ value: 'Avançado', label: 'Avançado' },
						]}
						{...getFieldProps('spanish')}
					/>
					<InputField
						id="courses"
						type="text"
						placeholder={'Liste aqui seus cursos...'}
						required={false}
						multiline={true}
						label={'Cursos'}
						className='my-16'
						{...getFieldProps('courses')}
					/>

					<ErrorList errors={formController.errors} touched={formController.touched} />

					{errors != null &&
						errors.map((err, index) => (
							<Typography key={`err-${index}`} variant="body2" color="error.dark">
								{err}
							</Typography>
						))}
					<div className="md:mt-24 mt-16">
						{edit ? (
							<Button type="submit" variant="primary" className="w-full">
								Salvar
							</Button>
						) : (
							<ButtonsPages
								submitDisabled={submitting}
								previousForm={'/candidato/form/professionalDataEducationIndex'}
							/>
						)}
					</div>
				</div>
			</form>
		</FormPage>
	);
};

export default ProfessionalData4Form;
